/**
 * Stale-deploy recovery for lazy route chunks.
 *
 * After a deploy, a tab still running the previous build asks for hashed
 * chunk files that no longer exist. Netlify's SPA fallback serves index.html
 * in their place, so the browser rejects the `import()` with a TypeError
 * ("Failed to fetch dynamically imported module" / "Importing a module script
 * failed" / "error loading dynamically imported module"). That is not an API
 * outage — the cure is a single full reload to pick up the new chunk map.
 */

const RELOAD_FLAG = 'realm:stale-chunk-reload'

/** True when the error is a failed dynamic `import()` of a route chunk. */
export function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false
  return /dynamically imported module|Importing a module script failed|Failed to fetch dynamically|ChunkLoadError|Loading chunk/i.test(
    err.message,
  )
}

/** A failure this soon after our own reload means the new build is broken, not stale. */
const RETRY_WINDOW_MS = 30_000

/**
 * Reload the page for the current URL and return true; return false (no
 * reload) if this URL already reloaded for a stale chunk in the last 30 s, so a
 * persistent failure surfaces as an error instead of a reload loop. The guard
 * expires by time rather than waiting to be consumed: a reload that worked
 * never clears it, and a leftover flag used to block recovery after the next
 * deploy.
 */
export function reloadOnceForStaleChunk(now: number = Date.now()): boolean {
  if (typeof window === 'undefined') return false
  const key = `${RELOAD_FLAG}:${window.location.pathname}`
  try {
    const last = Number(sessionStorage.getItem(key))
    if (last && now - last < RETRY_WINDOW_MS) {
      sessionStorage.removeItem(key)
      return false
    }
    sessionStorage.setItem(key, String(now))
  } catch {
    return false
  }
  window.location.reload()
  return true
}
